import { getDatabase, ref, update } from "firebase/database";
import { useAuth } from "../../contexts/AuthContext";
import React, { useState, useEffect } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import {
  adminVerificationEmail,
  BASS_BOAT_TYPE,
  buildVerificationAnswersPayload,
  clientVerificationEmail,
  getAllBoatTypes,
  getCurrentUserDetails,
  getRenterVerificationStatus,
  getUserMetaByID,
  getVerificationQuestionsForBoatType,
  getVerifiableBoatTypes,
  getActiveVerifiedBoatTypes,
  isVerificationStatusActive,
  OTHER_BOAT_PROOF_CATEGORIES,
  parseVerificationUploadedFiles,
  formatVerificationAnswerForDisplay,
  sendEmail,
} from "../utils";
import { notifyVerificationSubmitted } from "../utils/adminNotifications";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Alert } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";

const STATUS_LABELS = {
  Active: { label: "Verified", className: "verification-status--verified" },
  Pending: { label: "Pending Review", className: "verification-status--pending" },
  Reject: { label: "Denied", className: "verification-status--denied" },
  unset: { label: "Not Verified", className: "verification-status--unset" },
};

const getStatusMeta = (status) => {
  if (isVerificationStatusActive(status)) {
    return STATUS_LABELS.Active;
  }

  const normalized = String(status || "unset").toLowerCase();
  if (normalized === "pending") return STATUS_LABELS.Pending;
  if (normalized === "reject") return STATUS_LABELS.Reject;
  return STATUS_LABELS.unset;
};

const isPendingVerificationStatus = (status) =>
  String(status || "").toLowerCase() === "pending";

const getSavedAnswersForBoatType = (userMeta, boatType) => {
  const typeAnswers = userMeta?.boatVerifications?.[boatType]?.userAnswers;
  if (typeAnswers) {
    return typeAnswers;
  }

  if (boatType === BASS_BOAT_TYPE && userMeta?.userAnswers) {
    return userMeta.userAnswers;
  }

  return null;
};

const buildAnswerLookup = (savedAnswers) => {
  const answerMap = {};

  if (!savedAnswers) {
    return answerMap;
  }

  [
    ...(savedAnswers.multiChoiceQuestions || []),
    ...(savedAnswers.textQuestions || []),
    ...(savedAnswers.images || []),
  ].forEach((item) => {
    if (item?.question) {
      answerMap[item.question] = item.answer;
    }
  });

  return answerMap;
};

const getOtherBoatQuestionParts = (questions = []) => ({
  experienceOption: questions.find((question) => question.questionType === "option"),
  experienceText: questions.find((question) => question.questionType === "text"),
  proofUpload: questions.find((question) => question.questionType === "images"),
});

const getBassBoatQuestionParts = (questions = []) => ({
  experienceOption: questions.find(
    (question) => question.questionKey === "bass_experience"
  ),
  experienceText: questions.find(
    (question) => question.questionKey === "bass_experience_description"
  ),
  towCapableOption: questions.find(
    (question) => question.questionKey === "bass_tow_capable"
  ),
  towVehicle: questions.find((question) => question.questionKey === "bass_tow_vehicle"),
  boatsList: questions.find((question) => question.questionKey === "bass_boats_list"),
  proofUpload: questions.find((question) => question.questionKey === "bass_proof_upload"),
});

const EMPTY_TOW_VEHICLE = { make: "", model: "", year: "" };
const EMPTY_BASS_BOAT = { make: "", model: "", year: "", motorHp: "" };

const isOtherBoatType = (boatType) => boatType && boatType !== BASS_BOAT_TYPE;

const GetUserVerified = () => {
  const auth = useAuth();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [currentUser, setCurrentUser] = useState();
  const [userMeta, setUserMeta] = useState(null);
  const [boatTypes, setBoatTypes] = useState([]);
  const [selectedBoatType, setSelectedBoatType] = useState(BASS_BOAT_TYPE);
  const [allQuestionsData, setAllQuestions] = useState([]);
  const [textQuestionAnswers, setTextQuestionAnswers] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [uploadedImages, setUploadedImages] = useState({});
  const [proofUploadLoading, setProofUploadLoading] = useState(false);
  const [proofDragActive, setProofDragActive] = useState(false);
  const [towVehicle, setTowVehicle] = useState(EMPTY_TOW_VEHICLE);
  const [bassBoatsList, setBassBoatsList] = useState([{ ...EMPTY_BASS_BOAT }]);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [pageLoading, setPageLoading] = useState(true);

  const selectedStatus = getRenterVerificationStatus(
    currentUser,
    userMeta,
    selectedBoatType
  );
  const canSubmitForm =
    !isVerificationStatusActive(selectedStatus) &&
    !isPendingVerificationStatus(selectedStatus);
  const showReadOnlyAnswers =
    isVerificationStatusActive(selectedStatus) ||
    isPendingVerificationStatus(selectedStatus);
  const verifiedBoatTypes = getActiveVerifiedBoatTypes(currentUser, userMeta);
  const savedAnswers = getSavedAnswersForBoatType(userMeta, selectedBoatType);
  const savedAnswerLookup = buildAnswerLookup(savedAnswers);
  const isBassBoat = selectedBoatType === BASS_BOAT_TYPE;
  const bassBoatQuestions = getBassBoatQuestionParts(allQuestionsData);
  const otherBoatQuestions = getOtherBoatQuestionParts(allQuestionsData);
  const activeQuestions = isBassBoat ? bassBoatQuestions : otherBoatQuestions;
  const experienceAnswer = selectedOptions.find(
    (option) =>
      option.questionText === activeQuestions.experienceOption?.questionText
  )?.option;
  const towCapableAnswer = selectedOptions.find(
    (option) =>
      option.questionText === bassBoatQuestions.towCapableOption?.questionText
  )?.option;
  const experienceDescription =
    textQuestionAnswers.find(
      (answer) =>
        answer.questionText === activeQuestions.experienceText?.questionText
    )?.answer || "";
  const proofFiles =
    uploadedImages[activeQuestions.proofUpload?.questionText] || [];

  useEffect(() => {
    const loadPageData = async () => {
      try {
        setPageLoading(true);
        const [types, user, meta] = await Promise.all([
          getAllBoatTypes(),
          getCurrentUserDetails(),
          auth.currentUser
            ? getUserMetaByID(auth.currentUser.uid)
            : Promise.resolve(null),
        ]);

        const verifiableTypes = getVerifiableBoatTypes(types);
        setBoatTypes(verifiableTypes);
        setCurrentUser(user);
        setUserMeta(meta);

        const initialType = verifiableTypes.includes(BASS_BOAT_TYPE)
          ? BASS_BOAT_TYPE
          : verifiableTypes[0] || BASS_BOAT_TYPE;
        setSelectedBoatType(initialType);
      } catch (fetchError) {
        console.error("Error loading verification page:", fetchError);
        setError("Unable to load verification options. Please try again.");
      } finally {
        setPageLoading(false);
      }
    };

    loadPageData();
  }, [auth.currentUser]);

  useEffect(() => {
    const loadQuestions = async () => {
      if (!selectedBoatType) return;

      try {
        const questions = await getVerificationQuestionsForBoatType(
          selectedBoatType
        );
        setAllQuestions(questions);
        setTextQuestionAnswers([]);
        setSelectedOptions([]);
        setUploadedImages({});
        setProofUploadLoading(false);
        setProofDragActive(false);
        setTowVehicle({ ...EMPTY_TOW_VEHICLE });
        setBassBoatsList([{ ...EMPTY_BASS_BOAT }]);
        setPendingVerification(false);
        setValidationErrors({});
        setError("");
        setSuccess("");
      } catch (fetchError) {
        console.error("Error fetching questions:", fetchError);
      }
    };

    loadQuestions();
  }, [selectedBoatType]);

  const validateForm = () => {
    const errors = {};

    if (isBassBoat) {
      const {
        experienceOption,
        experienceText,
        towCapableOption,
        towVehicle: towVehicleQuestion,
        boatsList,
        proofUpload,
      } = bassBoatQuestions;

      if (experienceOption) {
        const optionIndex = allQuestionsData.findIndex(
          (question) => question.questionText === experienceOption.questionText
        );
        if (
          !selectedOptions.some(
            (option) => option.questionText === experienceOption.questionText
          )
        ) {
          errors[`question_${optionIndex}`] = "Please select an option.";
        }
      }

      if (experienceText && experienceAnswer === "Yes") {
        const textIndex = allQuestionsData.findIndex(
          (question) => question.questionText === experienceText.questionText
        );
        if (!experienceDescription.trim()) {
          errors[`question_${textIndex}`] =
            "Please describe your bass boat experience.";
        } else if (
          experienceText.maxLength &&
          experienceDescription.length > experienceText.maxLength
        ) {
          errors[`question_${textIndex}`] =
            `Please keep your response under ${experienceText.maxLength} characters.`;
        }
      }

      if (towCapableOption) {
        const towIndex = allQuestionsData.findIndex(
          (question) => question.questionText === towCapableOption.questionText
        );
        if (
          !selectedOptions.some(
            (option) => option.questionText === towCapableOption.questionText
          )
        ) {
          errors[`question_${towIndex}`] = "Please select an option.";
        }
      }

      if (towVehicleQuestion && towCapableAnswer === "Yes") {
        const vehicleIndex = allQuestionsData.findIndex(
          (question) => question.questionText === towVehicleQuestion.questionText
        );
        if (!towVehicle.make.trim() || !towVehicle.model.trim() || !towVehicle.year.trim()) {
          errors[`question_${vehicleIndex}`] =
            "Please provide your tow vehicle make, model, and year.";
        }
      }

      if (boatsList) {
        const boatsIndex = allQuestionsData.findIndex(
          (question) => question.questionText === boatsList.questionText
        );
        const hasCompleteBoat = bassBoatsList.some(
          (boat) =>
            boat.make.trim() &&
            boat.model.trim() &&
            boat.year.trim() &&
            boat.motorHp.trim()
        );
        if (!hasCompleteBoat) {
          errors[`question_${boatsIndex}`] =
            "Please add at least one complete bass boat entry.";
        }
      }

      if (proofUpload) {
        const imageIndex = allQuestionsData.findIndex(
          (question) => question.questionText === proofUpload.questionText
        );
        if (!proofFiles.length) {
          errors[`question_${imageIndex}`] = "Please upload at least one file.";
        }
      }
    } else if (isOtherBoatType(selectedBoatType)) {
      const { experienceOption, experienceText, proofUpload } =
        otherBoatQuestions;

      if (experienceOption) {
        const optionIndex = allQuestionsData.findIndex(
          (question) => question.questionText === experienceOption.questionText
        );
        if (
          !selectedOptions.some(
            (option) => option.questionText === experienceOption.questionText
          )
        ) {
          errors[`question_${optionIndex}`] = "Please select an option.";
        }
      }

      if (experienceText && experienceAnswer === "Yes") {
        const textIndex = allQuestionsData.findIndex(
          (question) => question.questionText === experienceText.questionText
        );
        if (!experienceDescription.trim()) {
          errors[`question_${textIndex}`] =
            "Please describe your boating experience.";
        } else if (
          experienceText.maxLength &&
          experienceDescription.length > experienceText.maxLength
        ) {
          errors[`question_${textIndex}`] =
            `Please keep your response under ${experienceText.maxLength} characters.`;
        }
      }

      if (proofUpload) {
        const imageIndex = allQuestionsData.findIndex(
          (question) => question.questionText === proofUpload.questionText
        );
        if (!proofFiles.length) {
          errors[`question_${imageIndex}`] = "Please upload at least one file.";
        }
      }
    }

    if (!pendingVerification) {
      errors.pendingVerification =
        "You must acknowledge pending verification.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (!name.startsWith("question_")) return;

    const questionIndex = parseInt(name.split("_")[1], 10);
    const questionText = allQuestionsData[questionIndex]?.questionText;
    if (!questionText) return;

    const existingAnswerIndex = textQuestionAnswers.findIndex(
      (answer) => answer.questionText === questionText
    );

    if (existingAnswerIndex !== -1) {
      setTextQuestionAnswers((prevAnswers) => {
        const updatedAnswers = [...prevAnswers];
        updatedAnswers[existingAnswerIndex] = {
          questionText,
          answer: value,
        };
        return updatedAnswers;
      });
    } else {
      setTextQuestionAnswers((prevAnswers) => [
        ...prevAnswers,
        { questionText, answer: value },
      ]);
    }
  };

  const handleRadioChange = (questionText, option) => {
    const existingOptionIndex = selectedOptions.findIndex(
      (selectedOption) => selectedOption.questionText === questionText
    );

    if (existingOptionIndex !== -1) {
      setSelectedOptions((prevOptions) => {
        const updatedOptions = [...prevOptions];
        updatedOptions[existingOptionIndex] = { questionText, option };
        return updatedOptions;
      });
    } else {
      setSelectedOptions((prevOptions) => [
        ...prevOptions,
        { questionText, option },
      ]);
    }
  };

  const uploadProofFile = async (questionText, file) => {
    const formData = new FormData();
    formData.append("file", file);

    const imageResponse = await fetch(
      `${process.env.REACT_APP_BASE_URL}/upload-featured-image`,
      {
        method: "POST",
        body: formData,
      }
    );

    const responseData = await imageResponse.json();
    const storedName = responseData?.file_name || "";

    if (!storedName) {
      throw new Error("Upload failed");
    }

    setUploadedImages((prev) => {
      const currentFiles = Array.isArray(prev[questionText])
        ? prev[questionText]
        : [];
      return {
        ...prev,
        [questionText]: [
          ...currentFiles,
          {
            storedName,
            originalName: file.name,
          },
        ],
      };
    });
  };

  const handleProofFilesSelected = async (questionText, fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || !questionText) return;

    try {
      setProofUploadLoading(true);
      setError("");
      for (const file of files) {
        await uploadProofFile(questionText, file);
      }
    } catch (uploadError) {
      console.error("Error uploading verification files:", uploadError);
      setError("Failed to upload one or more files. Please try again.");
    } finally {
      setProofUploadLoading(false);
    }
  };

  const handleRemoveProofFile = (questionText, fileIndex) => {
    setUploadedImages((prev) => {
      const currentFiles = Array.isArray(prev[questionText])
        ? [...prev[questionText]]
        : [];
      currentFiles.splice(fileIndex, 1);
      return {
        ...prev,
        [questionText]: currentFiles,
      };
    });
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setError("");
      setLoading(true);

      const structuredAnswers = {};
      if (isBassBoat) {
        if (bassBoatQuestions.towVehicle) {
          structuredAnswers[bassBoatQuestions.towVehicle.questionText] =
            towVehicle;
        }
        if (bassBoatQuestions.boatsList) {
          structuredAnswers[bassBoatQuestions.boatsList.questionText] =
            bassBoatsList.filter(
              (boat) =>
                boat.make.trim() ||
                boat.model.trim() ||
                boat.year.trim() ||
                boat.motorHp.trim()
            );
        }
      }

      const userAnswers = buildVerificationAnswersPayload(
        allQuestionsData,
        selectedOptions,
        textQuestionAnswers,
        uploadedImages,
        structuredAnswers
      );

      const userMetaRef = ref(
        getDatabase(),
        `userMeta/${auth.currentUser.uid}`
      );

      await update(userMetaRef, {
        [`boatVerifications/${selectedBoatType}`]: {
          status: "Pending",
          boatType: selectedBoatType,
          submittedAt: new Date().toISOString(),
          userAnswers,
        },
      });

      if (selectedBoatType === BASS_BOAT_TYPE) {
        const userRef = ref(getDatabase(), `users/${auth.currentUser.uid}`);
        await update(userRef, { status: "Pending" });
        await update(userMetaRef, { userAnswers });
      }

      const userFullName = `${currentUser.firstName} ${currentUser.lastName}`;
      const subject = `${selectedBoatType} Verification Application Submitted - ${userFullName}`;
      const to = currentUser.email;

      const emailBodyNew = await clientVerificationEmail(userFullName);
      await sendEmail(to, subject, emailBodyNew);

      const adminEmailBody = await adminVerificationEmail(userFullName);
      await sendEmail(
        "waivers@bbrboatrentals.com",
        `${selectedBoatType} Verification Application - ${userFullName}`,
        adminEmailBody
      );

      notifyVerificationSubmitted(
        userFullName,
        auth.currentUser.uid,
        selectedBoatType
      ).catch((err) =>
        console.error("Failed to create admin notification:", err)
      );

      setSuccess(
        `Your ${selectedBoatType} verification request has been submitted and is under review.`
      );

      const [user, meta] = await Promise.all([
        getCurrentUserDetails(),
        getUserMetaByID(auth.currentUser.uid),
      ]);
      setCurrentUser(user);
      setUserMeta(meta);
    } catch (submitError) {
      setError(submitError.message);
      setSuccess("");
    } finally {
      setLoading(false);
    }
  }

  const renderStatusMessage = (boatType, status) => {
    if (isVerificationStatusActive(status)) {
      return (
        <div className="verification-status-banner verification-status-banner--verified">
          <i className="material-icons" aria-hidden="true">
            check_circle
          </i>
          <p>You are verified for {boatType} rentals.</p>
        </div>
      );
    }

    if (isPendingVerificationStatus(status)) {
      return (
        <div className="verification-status-banner verification-status-banner--pending">
          <i className="material-icons" aria-hidden="true">
            schedule
          </i>
          <p>
            Your {boatType} verification is under review. You may still send
            booking inquiries, but your account will show as unverified to the
            boat owner.
          </p>
        </div>
      );
    }

    if (String(status || "").toLowerCase() === "reject") {
      const reason =
        userMeta?.boatVerifications?.[boatType]?.rejectionReason ||
        currentUser?.rejectionReason;
      return (
        <div className="verification-status-banner verification-status-banner--denied">
          <i className="material-icons" aria-hidden="true">
            cancel
          </i>
          <p>
            Your {boatType} verification was denied
            {reason ? `: ${reason}` : "."} Update your answers and submit
            again below.
          </p>
        </div>
      );
    }

    return null;
  };

  const renderReadOnlyAnswer = (question, answer) => {
    if (question.questionType === "images") {
      const files = parseVerificationUploadedFiles(answer);
      if (!files.length) {
        return <span className="text-muted">No files uploaded</span>;
      }

      return (
        <div className="verification-readonly-files">
          {files.map((file, index) => {
            const fileName = file.file || file.storedName || "";
            const displayName = file.name || file.originalName || fileName;
            const isPdf = String(fileName).toLowerCase().endsWith(".pdf");

            if (isPdf) {
              return (
                <a
                  key={`${fileName}-${index}`}
                  href={`${process.env.REACT_APP_BASE_URL}/images/${fileName}`}
                  target="_blank"
                  rel="noreferrer"
                  className="verification-readonly-file-link"
                >
                  {displayName}
                </a>
              );
            }

            return (
              <img
                key={`${fileName}-${index}`}
                src={`${process.env.REACT_APP_BASE_URL}/images/${fileName}`}
                alt={displayName}
                className="verification-readonly-image"
              />
            );
          })}
        </div>
      );
    }

    if (["structured", "boat_list"].includes(question.questionType)) {
      const formatted = formatVerificationAnswerForDisplay(question, answer);
      return (
        <span style={{ whiteSpace: "pre-wrap" }}>
          {formatted || <span className="text-muted">No answer provided</span>}
        </span>
      );
    }

    if (!answer) {
      return <span className="text-muted">No answer provided</span>;
    }

    return <span>{answer}</span>;
  };

  const renderProofUploadSection = (proofUpload, proofUploadIndex, subtitle) => {
    if (!proofUpload) return null;

    return (
      <div className="other-boat-proof-panel">
        <h6 className="other-boat-proof-title">{proofUpload.questionText}</h6>
        <p className="other-boat-proof-subtitle">{subtitle}</p>

        <div className="other-boat-proof-categories">
          {OTHER_BOAT_PROOF_CATEGORIES.map((category) => (
            <div key={category.title} className="other-boat-proof-category">
              <i className="material-icons" aria-hidden="true">
                {category.icon}
              </i>
              <div>
                <strong>{category.title}</strong>
                <p>{category.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`other-boat-dropzone${
            proofDragActive ? " is-drag-active" : ""
          }`}
          onDragOver={(event) => {
            event.preventDefault();
            setProofDragActive(true);
          }}
          onDragLeave={() => setProofDragActive(false)}
          onDrop={(event) => {
            event.preventDefault();
            setProofDragActive(false);
            handleProofFilesSelected(
              proofUpload.questionText,
              event.dataTransfer.files
            );
          }}
        >
          <i className="material-icons" aria-hidden="true">
            cloud_upload
          </i>
          <p className="other-boat-dropzone-title">
            Choose files or drag and drop here
          </p>
          <p className="other-boat-dropzone-subtitle">
            You can upload multiple files (JPG, PNG, PDF).
          </p>
          <label className="other-boat-dropzone-button">
            Browse files
            <input
              type="file"
              accept={
                proofUpload.accept ||
                "image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
              }
              multiple
              hidden
              onChange={(event) => {
                handleProofFilesSelected(
                  proofUpload.questionText,
                  event.target.files
                );
                event.target.value = "";
              }}
            />
          </label>
        </div>

        {proofUploadLoading && (
          <p className="other-boat-upload-status">Uploading files...</p>
        )}

        {proofFiles.length > 0 && (
          <ul className="other-boat-uploaded-files">
            {proofFiles.map((file, index) => (
              <li key={`${file.storedName}-${index}`}>
                <span>{file.originalName}</span>
                <button
                  type="button"
                  className="other-boat-remove-file"
                  onClick={() =>
                    handleRemoveProofFile(proofUpload.questionText, index)
                  }
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="other-boat-proof-note">
          <i className="material-icons" aria-hidden="true">
            verified_user
          </i>
          <p>
            Only genuine documents and photos are accepted. AI-generated or
            AI-altered images may be automatically detected and rejected.
          </p>
        </div>

        {validationErrors[`question_${proofUploadIndex}`] && (
          <Alert severity="error" className="mt-2 mb-0">
            {validationErrors[`question_${proofUploadIndex}`]}
          </Alert>
        )}
      </div>
    );
  };

  const renderYesNoQuestion = (question, selectedValue, optionIndex) => {
    if (!question) return null;

    return (
      <Form.Group className="other-boat-experience-group">
        {question.helperText && (
          <p className="bass-boat-helper-text">{question.helperText}</p>
        )}
        <Form.Label className="other-boat-question-label">
          {question.questionText}
        </Form.Label>
        <div className="other-boat-yes-no-group">
          {question.options.map((option) => (
            <button
              key={option}
              type="button"
              className={`other-boat-yes-no-btn${
                selectedValue === option ? " is-selected" : ""
              }`}
              onClick={() => handleRadioChange(question.questionText, option)}
            >
              {option}
            </button>
          ))}
        </div>
        {validationErrors[`question_${optionIndex}`] && (
          <Alert severity="error" className="mt-2 mb-0">
            {validationErrors[`question_${optionIndex}`]}
          </Alert>
        )}
      </Form.Group>
    );
  };

  const renderExperienceTextarea = (experienceText, experienceTextIndex) => {
    if (!experienceText) return null;
    const maxLength = experienceText.maxLength || 1000;

    return (
      <Form.Group className="other-boat-experience-group">
        <Form.Label className="other-boat-question-label">
          {experienceText.questionText}
        </Form.Label>
        <div className="other-boat-textarea-wrap">
          <Form.Control
            as="textarea"
            rows={5}
            name={`question_${experienceTextIndex}`}
            placeholder={
              experienceText.placeholder || "Describe your experience..."
            }
            maxLength={maxLength}
            value={experienceDescription}
            onChange={handleChange}
          />
          <span className="other-boat-char-count">
            {experienceDescription.length} / {maxLength}
          </span>
        </div>
        {validationErrors[`question_${experienceTextIndex}`] && (
          <Alert severity="error" className="mt-2 mb-0">
            {validationErrors[`question_${experienceTextIndex}`]}
          </Alert>
        )}
      </Form.Group>
    );
  };

  const renderVerificationAcknowledgement = () => (
    <Row className="mt-4">
      <FormGroup className="verification-acknowledgement w-100">
        <div className="verification-acknowledgement-box">
          <FormControlLabel
            className="verification-acknowledgement-check"
            control={
              <Checkbox
                checked={pendingVerification}
                onChange={(event) =>
                  setPendingVerification(event.target.checked)
                }
              />
            }
            label=""
          />
          <div>
            <p className="verification-acknowledgement-title">
              <span className="verification-acknowledgement-tag">
                Pending Verification
              </span>
              <span className="text-danger">*</span>
            </p>
            <p className="verification-acknowledgement-text">
              Your {selectedBoatType} verification will be under review. During
              this time you may still send booking inquiries, but your account
              will show as unverified to the boat owner.
            </p>
            {validationErrors.pendingVerification && (
              <Alert severity="error" className="mt-2 mb-0">
                {validationErrors.pendingVerification}
              </Alert>
            )}
          </div>
        </div>
      </FormGroup>
    </Row>
  );

  const renderVerificationSubmit = (submitLabel) => (
    <Row>
      <Col>
        <div className="verification-submit-row mt-4">
          <Button
            className="verification-submit-btn"
            variant="dark"
            type="submit"
            disabled={proofUploadLoading}
          >
            {loading ? (
              <ThreeDots
                visible={true}
                height="22"
                width="22"
                color="#fff"
                radius="20"
                ariaLabel="three-dots-loading"
              />
            ) : (
              submitLabel
            )}
          </Button>
          <p className="verification-security-note mb-0">
            <i className="material-icons" aria-hidden="true">
              lock
            </i>
            Your information is secure and will only be used for verification
            purposes.
          </p>
        </div>
        {success && (
          <Alert className="mt-3" severity="success">
            {success}
          </Alert>
        )}
      </Col>
    </Row>
  );

  const renderOtherBoatVerificationForm = () => {
    const { experienceOption, experienceText, proofUpload } = otherBoatQuestions;
    const experienceOptionIndex = allQuestionsData.findIndex(
      (question) => question.questionText === experienceOption?.questionText
    );
    const experienceTextIndex = allQuestionsData.findIndex(
      (question) => question.questionText === experienceText?.questionText
    );
    const proofUploadIndex = allQuestionsData.findIndex(
      (question) => question.questionText === proofUpload?.questionText
    );

    return (
      <Form onSubmit={handleSubmit} className="verification-form mt-4">
        <div className="verification-form-intro">
          <h5 className="mb-2">{selectedBoatType} Verification</h5>
          <p className="text-muted mb-0">
            Answer the questions below and upload proof of your boating
            experience. Only the person operating the boat needs to be
            verified.
          </p>
        </div>

        {error && <Alert severity="error">{error}</Alert>}

        {renderYesNoQuestion(
          experienceOption,
          experienceAnswer,
          experienceOptionIndex
        )}
        {renderExperienceTextarea(experienceText, experienceTextIndex)}
        {renderProofUploadSection(
          proofUpload,
          proofUploadIndex,
          "Please upload one or more of the following:"
        )}

        {renderVerificationAcknowledgement()}
        {renderVerificationSubmit("Submit for Verification")}
      </Form>
    );
  };

  const renderBassBoatVerificationForm = () => {
    const {
      experienceOption,
      experienceText,
      towCapableOption,
      towVehicle: towVehicleQuestion,
      boatsList,
      proofUpload,
    } = bassBoatQuestions;
    const experienceOptionIndex = allQuestionsData.findIndex(
      (question) => question.questionText === experienceOption?.questionText
    );
    const experienceTextIndex = allQuestionsData.findIndex(
      (question) => question.questionText === experienceText?.questionText
    );
    const towCapableIndex = allQuestionsData.findIndex(
      (question) => question.questionText === towCapableOption?.questionText
    );
    const towVehicleIndex = allQuestionsData.findIndex(
      (question) => question.questionText === towVehicleQuestion?.questionText
    );
    const boatsListIndex = allQuestionsData.findIndex(
      (question) => question.questionText === boatsList?.questionText
    );
    const proofUploadIndex = allQuestionsData.findIndex(
      (question) => question.questionText === proofUpload?.questionText
    );

    return (
      <Form onSubmit={handleSubmit} className="verification-form mt-4">
        <div className="verification-form-intro">
          <h5 className="mb-2">Bass Boat Verification</h5>
          <p className="text-muted mb-0">
            Answer the questions below and upload proof of your boating
            experience. Only the person operating the boat needs to be
            verified.
          </p>
        </div>

        {error && <Alert severity="error">{error}</Alert>}

        {renderYesNoQuestion(
          experienceOption,
          experienceAnswer,
          experienceOptionIndex
        )}
        {renderExperienceTextarea(experienceText, experienceTextIndex)}

        <div className="bass-boat-tow-section">
          {renderYesNoQuestion(
            towCapableOption,
            towCapableAnswer,
            towCapableIndex
          )}

          {towVehicleQuestion && towCapableAnswer === "Yes" && (
            <Form.Group className="other-boat-experience-group">
              <Form.Label className="other-boat-question-label">
                {towVehicleQuestion.subtitle}
              </Form.Label>
              <div className="bass-boat-field-grid bass-boat-field-grid--three">
                {towVehicleQuestion.fields.map((field) => (
                  <div key={field.key}>
                    <Form.Label className="bass-boat-field-label">
                      {field.label}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={field.placeholder}
                      value={towVehicle[field.key] || ""}
                      onChange={(event) =>
                        setTowVehicle((prev) => ({
                          ...prev,
                          [field.key]: event.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
              {validationErrors[`question_${towVehicleIndex}`] && (
                <Alert severity="error" className="mt-2 mb-0">
                  {validationErrors[`question_${towVehicleIndex}`]}
                </Alert>
              )}
            </Form.Group>
          )}
        </div>

        {boatsList && (
          <Form.Group className="other-boat-experience-group">
            <Form.Label className="other-boat-question-label">
              {boatsList.questionText}
            </Form.Label>
            <p className="bass-boat-helper-text">{boatsList.subtitle}</p>

            {bassBoatsList.map((boat, boatIndex) => (
              <div key={`bass-boat-${boatIndex}`} className="bass-boat-list-row">
                <div className="bass-boat-field-grid bass-boat-field-grid--four">
                  {boatsList.fields.map((field) => (
                    <div key={field.key}>
                      <Form.Label className="bass-boat-field-label">
                        {field.label}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={field.placeholder}
                        value={boat[field.key] || ""}
                        onChange={(event) =>
                          setBassBoatsList((prev) => {
                            const updated = [...prev];
                            updated[boatIndex] = {
                              ...updated[boatIndex],
                              [field.key]: event.target.value,
                            };
                            return updated;
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
                {bassBoatsList.length > 1 && (
                  <button
                    type="button"
                    className="bass-boat-remove-btn"
                    onClick={() =>
                      setBassBoatsList((prev) =>
                        prev.filter((_, index) => index !== boatIndex)
                      )
                    }
                  >
                    Remove boat
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              className="bass-boat-add-btn"
              onClick={() =>
                setBassBoatsList((prev) => [...prev, { ...EMPTY_BASS_BOAT }])
              }
            >
              + Add Another Boat
            </button>

            {validationErrors[`question_${boatsListIndex}`] && (
              <Alert severity="error" className="mt-2 mb-0">
                {validationErrors[`question_${boatsListIndex}`]}
              </Alert>
            )}
          </Form.Group>
        )}

        {renderProofUploadSection(
          proofUpload,
          proofUploadIndex,
          proofUpload?.proofSubtitle ||
            "Please upload one or more of the following (screenshots and photos are accepted):"
        )}

        {renderVerificationAcknowledgement()}
        {renderVerificationSubmit("Submit for Verification")}
      </Form>
    );
  };

  const renderQuestionsSection = () => (
    <div className="verification-form mt-4">
      <div className="verification-form-intro">
        <h6 className="mb-2">{selectedBoatType} verification</h6>
        <p className="text-muted mb-0">
          {showReadOnlyAnswers
            ? "Your submitted answers for this boat type."
            : isBassBoat
              ? "Answer the questions below and upload proof of your boating experience. Only the person operating the boat needs to be verified."
              : "Answer the questions below and upload proof of your boating experience. Only the person operating the boat needs to be verified."}
        </p>
      </div>

      {allQuestionsData.length === 0 ? (
        <p className="text-muted mt-3 mb-0">
          No verification questions are configured for this boat type yet.
        </p>
      ) : (
        <div className="verification-readonly-list mt-3">
          {allQuestionsData.map((question, index) => (
            <div
              key={`${selectedBoatType}-${index}`}
              className="verification-readonly-item"
            >
              <div className="verification-readonly-question">
                {question.questionText}
              </div>
              <div className="verification-readonly-answer">
                {renderReadOnlyAnswer(
                  question,
                  savedAnswerLookup[question.questionText]
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (pageLoading) {
    return <div className="p-3">Loading verification options...</div>;
  }

  return (
    <>
      <div className="page-description page-description-tabbed">
        <h1 className="mb-0">Get Verified</h1>
        <p className="text-muted mb-0">
          Verify your experience by boat type to build trust with boat owners.
        </p>
      </div>

      <hr />
      <div className="auth-credentials m-b-xxl">
        <Row className="card verification-page-card border-0 shadow-sm">
          <Col className="p-4">
            <div className="verification-section-header">
              <h5 className="mb-1">Select boat type</h5>
              <p className="text-muted mb-0">
                Choose a category to view status or submit verification.
              </p>
            </div>

            <div className="verification-boat-type-grid">
              {boatTypes.map((boatType) => {
                const status = getRenterVerificationStatus(
                  currentUser,
                  userMeta,
                  boatType
                );
                const statusMeta = getStatusMeta(status);
                const isSelected = selectedBoatType === boatType;

                return (
                  <button
                    key={boatType}
                    type="button"
                    className={`verification-boat-type-card${
                      isSelected ? " is-selected" : ""
                    }`}
                    onClick={() => setSelectedBoatType(boatType)}
                    aria-pressed={isSelected}
                  >
                    <span className="verification-boat-type-card__name">
                      {boatType}
                    </span>
                    <span
                      className={`verification-status-pill ${statusMeta.className}`}
                    >
                      {statusMeta.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {verifiedBoatTypes.length > 0 && (
              <div className="verification-summary-banner">
                <i className="material-icons" aria-hidden="true">
                  verified
                </i>
                <div>
                  <strong>Verified boat types</strong>
                  <p>{verifiedBoatTypes.join(" · ")}</p>
                </div>
              </div>
            )}

            {renderStatusMessage(selectedBoatType, selectedStatus)}

            {showReadOnlyAnswers && renderQuestionsSection()}

            {canSubmitForm &&
              (isBassBoat
                ? renderBassBoatVerificationForm()
                : renderOtherBoatVerificationForm())}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default GetUserVerified;
